import { createContext, useContext, useState, type ReactNode } from 'react';
import { readPersistedJson, serializeForStorage } from '../lib/persistSession';

export interface User {
    id: string;
    name: string;
    email: string;
    status: string;
    visitCount: number;
    avatar?: string;
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (user: User) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function readStoredUser(): User | null {
    const parsed = readPersistedJson<User>('user');
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(readStoredUser);
    const [isLoading] = useState(false);

    const login = (newUser: User) => {
        setUser(newUser);
        localStorage.setItem('user', serializeForStorage(newUser));
    };

    const logout = () => {
        setUser(null);
        localStorage.removeItem('user');
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated: !!user,
                isLoading,
                login,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
